import React from "react";
import { Badge, BadgeProps } from "@/components/ui/badge";
import { EmployeeStatus } from '@/contexts/employees/EmployeesContext';
import { OvertimeStatus } from '@/types/enums';
import { CheckIcon, XIcon, Clock, UserCheck, Shield, Crown } from "lucide-react";

// Status configuration interface
interface StatusConfig {
  variant: BadgeProps['variant'];
  className: string;
  icon?: React.ReactNode;
  label: string;
}

// Status configuration mapping
const getStatusConfig = (status: string): StatusConfig => {
  switch (status.toLowerCase()) {
    case OvertimeStatus.APPROVED:
      return {
        variant: "default",
        className: "bg-green-100 text-green-800 hover:bg-green-200 border-green-300",
        icon: <CheckIcon className="h-3 w-3" />,
        label: "Approved"
      };
    case OvertimeStatus.REJECTED:
      return {
        variant: "destructive",
        className: "bg-red-100 text-red-800 hover:bg-red-200 border-red-300",
        icon: <XIcon className="h-3 w-3" />,
        label: "Rejected"
      };
    case OvertimeStatus.PENDING:
      return {
        variant: "outline",
        className: "bg-yellow-50 text-yellow-700 border-yellow-300",
        icon: <Clock className="h-3 w-3" />,
        label: "Pending"
      };
    case OvertimeStatus.PENDING_VP_APPROVAL:
      return {
        variant: "outline",
        className: "bg-purple-50 text-purple-700 border-purple-300",
        icon: <Crown className="h-3 w-3" />,
        label: "Pending VP"
      };
    case OvertimeStatus.PENDING_DH_APPROVAL:
      return {
        variant: "outline",
        className: "bg-blue-50 text-blue-700 border-blue-300",
        icon: <UserCheck className="h-3 w-3" />,
        label: "Pending DH"
      };
    case OvertimeStatus.PENDING_PM_APPROVAL:
      return {
        variant: "outline",
        className: "bg-indigo-50 text-indigo-700 border-indigo-300",
        icon: <Shield className="h-3 w-3" />,
        label: "Pending PM"
      };
    case EmployeeStatus.ACTIVE.toLowerCase():
      return {
        variant: "default",
        className: "bg-green-600 hover:bg-green-700",
        icon: <CheckIcon className="h-3 w-3" />,
        label: EmployeeStatus.ACTIVE
      };
    case 'on leave':
      return {
        variant: "outline",
        className: "bg-yellow-500 hover:bg-yellow-600",
        icon: <Clock className="h-3 w-3" />,
        label: "On Leave"
      };
    case EmployeeStatus.TERMINATED.toLowerCase():
      return {
        variant: "destructive",
        className: "bg-red-600 hover:bg-red-700",
        icon: <XIcon className="h-3 w-3" />,
        label: EmployeeStatus.TERMINATED
      };
    case EmployeeStatus.SUSPENDED.toLowerCase():
      return {
        variant: "outline",
        className: "bg-orange-500 hover:bg-orange-600",
        icon: <Clock className="h-3 w-3" />,
        label: EmployeeStatus.SUSPENDED
      };
    default:
      return {
        variant: "outline",
        className: "bg-gray-100 text-gray-800 border-gray-300",
        label: status
      };
  }
};

// Reusable StatusBadge component
export interface StatusBadgeProps {
  status: string;
  showIcon?: boolean;
  className?: string;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ 
  status, 
  showIcon = true, 
  className = "" 
}) => {
  const config = getStatusConfig(status);
  
  return (
    <Badge 
      variant={config.variant} 
      className={`${config.className} ${className}`}
    >
      {showIcon && config.icon && (
        <span className="mr-1">{config.icon}</span>
      )}
      {config.label}
    </Badge>
  );
};

// Legacy function for backward compatibility
export const getStatusBadge = (status: string) => {
  return <StatusBadge status={status} />;
};

// Status color utilities for different use cases
export const getStatusColor = (status: string, variant: 'solid' | 'light' = 'light'): string => {
  switch (status.toLowerCase()) {
    case OvertimeStatus.APPROVED:
      return variant === 'solid' 
        ? 'bg-green-600 hover:bg-green-700' 
        : 'bg-green-100 text-green-800';
    case OvertimeStatus.REJECTED:
      return variant === 'solid' 
        ? 'bg-red-500 hover:bg-red-600' 
        : 'bg-red-100 text-red-800';
    case OvertimeStatus.PENDING:
      return variant === 'solid' 
        ? 'bg-orange-500 hover:bg-orange-600' 
        : 'bg-yellow-100 text-yellow-800';
    case OvertimeStatus.PENDING_VP_APPROVAL:
      return variant === 'solid' 
        ? 'bg-purple-500 hover:bg-purple-600' 
        : 'bg-purple-100 text-purple-800';
    case OvertimeStatus.PENDING_DH_APPROVAL:
      return variant === 'solid' 
        ? 'bg-blue-500 hover:bg-blue-600' 
        : 'bg-blue-100 text-blue-800';
    case OvertimeStatus.PENDING_PM_APPROVAL:
      return variant === 'solid' 
        ? 'bg-indigo-500 hover:bg-indigo-600' 
        : 'bg-indigo-100 text-indigo-800';
    case EmployeeStatus.ACTIVE.toLowerCase():
      return variant === 'solid' 
        ? 'bg-green-600 hover:bg-green-700' 
        : 'bg-green-100 text-green-800';
    case 'on leave':
      return variant === 'solid' 
        ? 'bg-yellow-500 hover:bg-yellow-600' 
        : 'bg-yellow-100 text-yellow-800';
    case EmployeeStatus.TERMINATED.toLowerCase():
      return variant === 'solid' 
        ? 'bg-red-600 hover:bg-red-700' 
        : 'bg-red-100 text-red-800';
    case EmployeeStatus.SUSPENDED.toLowerCase():
      return variant === 'solid' 
        ? 'bg-orange-500 hover:bg-orange-600' 
        : 'bg-orange-100 text-orange-800';
    case 'positive':
      return variant === 'solid' 
        ? 'bg-green-500 hover:bg-green-600' 
        : 'bg-green-100 text-green-800';
    case 'negative':
      return variant === 'solid' 
        ? 'bg-red-500 hover:bg-red-600' 
        : 'bg-red-100 text-red-800';
    case 'info':
      return variant === 'solid' 
        ? 'bg-blue-500 hover:bg-blue-600' 
        : 'bg-blue-50 text-blue-700';
    case 'warning':
      return variant === 'solid' 
        ? 'bg-amber-500 hover:bg-amber-600' 
        : 'bg-amber-100 text-amber-800';
    case 'neutral':
      return variant === 'solid' 
        ? 'bg-gray-500 hover:bg-gray-600' 
        : 'bg-gray-50 text-gray-700';
    default:
      return variant === 'solid' 
        ? 'bg-gray-500 hover:bg-gray-600' 
        : 'bg-gray-100 text-gray-800';
  }
};

// Chart-specific status colors (for analytics and charts)
export const getStatusChartColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case OvertimeStatus.APPROVED:
      return 'hsl(142, 76%, 36%)'; // Green
    case OvertimeStatus.REJECTED:
      return 'hsl(0, 84%, 60%)'; // Red
    case OvertimeStatus.PENDING:
      return 'hsl(45, 93%, 47%)'; // Yellow/Orange
    case OvertimeStatus.PENDING_VP_APPROVAL:
      return 'hsl(262, 83%, 58%)'; // Purple
    case OvertimeStatus.PENDING_DH_APPROVAL:
      return 'hsl(217, 91%, 60%)'; // Blue
    case OvertimeStatus.PENDING_PM_APPROVAL:
      return 'hsl(238, 100%, 67%)'; // Indigo
    default:
      return 'hsl(210, 40%, 50%)'; // Gray
  }
};